import React, { useState, useEffect } from 'react';
import { TextField, Flex, Text, TextArea, Button } from "@radix-ui/themes";
import Icon from '@mdi/react';
import { mdiContentSave, mdiClose, mdiDelete } from '@mdi/js';
import { api } from '../services/api';
import { useStore } from '../store/store';
import { useParams } from 'react-router-dom';

export const SidebarNoteEdit = (props) => {
    const [state, actions] = useStore();
    const [temporaryNote, setTemporaryNote] = useState(props.selectedNote);
    const [focusedField, setFocusedField] = useState(null);
    const [saving, setSaving] = useState(false);
    
    useEffect(() => {
        setTemporaryNote(props.selectedNote);
    }, [props.selectedNote]);

    const saveNote = async () => {
        try {
            setSaving(true);

            const novelId = state.currentNovel?.id;
            
            // Save to API
            let response;
            if (temporaryNote.new === true) {
                response = await api.createNote(
                    novelId,
                    temporaryNote.title || 'Untitled Note',
                    temporaryNote.content || ''
                );
                
                if (response.success) {
                    // Fetch the newly created note to get its complete data
                    const noteResponse = await api.getNoteById(response.noteId);
                    if (noteResponse.success) {
                        // Update the current novel in the store
                        const currentNotes = state.currentNovel?.notes || [];
                        const updatedNotes = [...currentNotes, noteResponse.note];
                        
                        actions.setCurrentNovel({
                            ...state.currentNovel,
                            notes: updatedNotes
                        });
                        
                        // Close the note editor
                        props.setSelectedNote(null);
                    }
                }
            } else {
                // Update existing note
                response = await api.updateNote(
                    temporaryNote.id,
                    temporaryNote.title || 'Untitled Note',
                    temporaryNote.content || ''
                );
                
                if (response.success) {
                    // Update the note in the current novel's notes array
                    const currentNotes = state.currentNovel?.notes || [];
                    const updatedNotes = currentNotes.map(note => 
                        note.id === temporaryNote.id ? temporaryNote : note
                    );
                    
                    actions.setCurrentNovel({
                        ...state.currentNovel,
                        notes: updatedNotes
                    });
                    
                    // Close the note editor
                    props.setSelectedNote(null);
                }
            }
        } catch (error) {
            console.error("Failed to save note:", error);
        } finally {
            setSaving(false);
        }
    }

    const deleteNote = async () => {
        if (!temporaryNote.id || temporaryNote.new) {
            // If it's a new note that hasn't been saved yet, just close it
            props.setSelectedNote(null);
            return;
        }
        
        try {
            const response = await api.deleteNote(temporaryNote.id);
            
            if (response.success) {
                // Remove the note from the current novel's notes array
                const currentNotes = state.currentNovel?.notes || [];
                const updatedNotes = currentNotes.filter(note => note.id !== temporaryNote.id);
                
                actions.setCurrentNovel({
                    ...state.currentNovel,
                    notes: updatedNotes
                });
                
                // Close the note editor
                props.setSelectedNote(null);
            }
        } catch (error) {
            console.error("Failed to delete note:", error);
        }
    }

    return (
        <div className='sidebar-sidebar'>
            <div style={{textAlign: 'right', cursor: 'pointer'}} onClick={() => props.setSelectedNote(null)}>
                <Icon path={mdiClose} color={'#FFFFFF50'} size={1} />
            </div>
            <div className="sidebar-form">
                <Flex direction="column" align={'start'}>
                    <Text size={"2"} className="sidebar-form-textInput-label">Title</Text>
                    {state.readOnly ? (
                        <div className="sidebar-form-textInput read-only-field">{temporaryNote?.title || ''}</div>
                    ) : (
                        <TextField.Root style={{width: "300px"}} className="sidebar-form-textInput" 
                            placeholder="Note Title" 
                            value={temporaryNote?.title || ''} 
                            variant="soft" 
                            onChange={(e) => {
                                setTemporaryNote({...temporaryNote, title: e.target.value});
                            }} 
                        />
                    )}
                </Flex>
                <br />
                <Flex direction="column" align={'start'}>
                    <Text size={"2"} as="div" className="sidebar-form-textInput-label">Content</Text>
                    {state.readOnly ? (
                        <div className="sidebar-form-textInput read-only-field" style={{ minHeight: "300px" }}>
                            {temporaryNote?.content || ''}
                        </div>
                    ) : (
                        <TextArea 
                            className={`sidebar-form-textInput sidebar-form-textInput-big`} 
                            placeholder="Note content..." 
                            value={temporaryNote?.content || ''} 
                            variant="soft" 
                            style={{ minHeight: "300px" }}
                            onFocus={() => setFocusedField('content')}
                            onBlur={() => setFocusedField(null)}
                            onChange={(e) => {
                                setTemporaryNote({...temporaryNote, content: e.target.value});
                            }} 
                        />
                    )}
                </Flex>
            </div>
            {!state.readOnly && (
                <div>
                    <Flex justify={'between'} gap="20px">
                        <Button color="green" 
                            style={{width: '80%', marginTop: '20px', cursor: 'pointer'}} 
                            onClick={saveNote}
                            disabled={saving}
                        >
                            <Icon path={mdiContentSave} size={1} />
                            {saving ? 'Saving...' : 'Save Note'}
                        </Button>
                        <Button color="red" 
                            style={{width: 'calc(20% - 20px)', marginTop: '20px', cursor: 'pointer'}} 
                            onClick={deleteNote}
                            disabled={saving}
                        >
                            <Icon path={mdiDelete} size={1} />
                        </Button>
                    </Flex>
                </div>
            )}
        </div>
    );
};

export default SidebarNoteEdit;
